import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import EdpiesLogo from '@/components/EdpiesLogo';
import { ArrowLeft, Download, QrCode, Copy, Check, Printer, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QRCodePage = () => {
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState(256);
  const [selectedPage, setSelectedPage] = useState('register');
  
  const pages = {
    register: { label: 'Registration Page', path: '/auth', description: 'Direct link to sign up' },
    tutor: { label: 'Become a Tutor', path: '/become-a-tutor', description: 'Tutor recruitment page' },
    home: { label: 'Homepage', path: '/', description: 'Main landing page' },
    contact: { label: 'Contact Us', path: '/contact', description: 'Contact information' }
  };
  
  const getFullUrl = () => {
    const baseUrl = BACKEND_URL.replace('/api', '').replace(':8001', '');
    return `${baseUrl}${pages[selectedPage].path}`;
  };

  const downloadQRCode = (format) => {
    const canvas = document.getElementById('qr-canvas');
    if (!canvas) return;
    
    const url = canvas.toDataURL(format === 'png' ? 'image/png' : 'image/jpeg', 1.0);
    const link = document.createElement('a');
    link.download = `edpies-qr-${selectedPage}.${format}`;
    link.href = url;
    link.click();
    toast.success(`QR Code downloaded as ${format.toUpperCase()}`);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getFullUrl());
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const printQRCode = () => {
    const printWindow = window.open('', '_blank');
    const canvas = document.getElementById('qr-canvas');
    const dataUrl = canvas.toDataURL('image/png');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Edpies QR Code - ${pages[selectedPage].label}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .qr-container {
              text-align: center;
              padding: 40px;
              border: 2px solid #e5e7eb;
              border-radius: 16px;
            }
            .logo {
              font-size: 32px;
              font-weight: bold;
              color: #7c3aed;
              margin-bottom: 20px;
            }
            .qr-image {
              margin: 20px 0;
            }
            .scan-text {
              font-size: 18px;
              color: #374151;
              margin-top: 16px;
            }
            .url {
              font-size: 12px;
              color: #6b7280;
              margin-top: 8px;
              word-break: break-all;
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="logo">e. Edpies</div>
            <img src="${dataUrl}" class="qr-image" width="200" height="200" />
            <div class="scan-text">Scan to ${pages[selectedPage].description}</div>
            <div class="url">${getFullUrl()}</div>
          </div>
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <EdpiesLogo size="sm" />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
              Edpies
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* QR Code Display */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="text-center">
              <CardTitle className="text-white flex items-center justify-center gap-2">
                <QrCode className="h-6 w-6 text-purple-400" />
                QR Code Generator
              </CardTitle>
              <CardDescription className="text-slate-400">
                Scan to access Edpies instantly
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              {/* QR Code */}
              <div className="bg-white p-6 rounded-2xl shadow-xl mb-6">
                <QRCodeCanvas
                  id="qr-canvas"
                  value={getFullUrl()}
                  size={qrSize}
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: "",
                    height: 0,
                    width: 0,
                    excavate: false,
                  }}
                />
              </div>
              
              {/* Page Label */}
              <div className="text-center mb-4">
                <p className="text-lg font-semibold text-white">{pages[selectedPage].label}</p>
                <p className="text-sm text-slate-400">{pages[selectedPage].description}</p>
              </div>

              {/* URL Display */}
              <div className="w-full bg-slate-900 rounded-lg p-3 flex items-center gap-2">
                <Input 
                  value={getFullUrl()} 
                  readOnly 
                  className="bg-transparent border-none text-slate-300 text-sm"
                />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={copyLink}
                  className="text-slate-400 hover:text-white"
                >
                  {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Controls */}
          <div className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Customize QR Code</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Page Selection */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Link Destination</Label>
                  <Select value={selectedPage} onValueChange={setSelectedPage}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(pages).map(([key, page]) => (
                        <SelectItem key={key} value={key}>{page.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Size Selection */}
                <div className="space-y-2">
                  <Label className="text-slate-300">QR Code Size</Label>
                  <Select value={qrSize.toString()} onValueChange={(v) => setQrSize(parseInt(v))}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="128">Small (128px)</SelectItem>
                      <SelectItem value="256">Medium (256px)</SelectItem>
                      <SelectItem value="512">Large (512px)</SelectItem>
                      <SelectItem value="1024">Extra Large (1024px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Download Options */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Download & Share</CardTitle>
                <CardDescription className="text-slate-400">
                  Use for business cards, flyers, and promotional materials
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  onClick={() => downloadQRCode('png')} 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PNG (Best for Print)
                </Button>
                <Button 
                  onClick={() => downloadQRCode('jpg')} 
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download JPG
                </Button>
                <Button 
                  onClick={printQRCode} 
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print QR Code
                </Button>
                <Button 
                  onClick={copyLink} 
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </CardContent>
            </Card>

            {/* Usage Tips */}
            <Card className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-purple-700/50">
              <CardContent className="p-4">
                <h4 className="text-white font-semibold mb-2">💡 Usage Tips</h4>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>• Use <strong>Large (512px)</strong> for posters and banners</li>
                  <li>• Use <strong>Medium (256px)</strong> for business cards</li>
                  <li>• PNG format is best for print materials</li>
                  <li>• Ensure adequate white space around QR code</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodePage;
